/**
 * ByJurisdictionView: shared jurisdiction cards used in both the entities
 * aggregate dashboard and the group scope Jurisdiction tab.
 *
 * Props:
 *   countryConfig  - { UK: { name, flag, currency, color, country, entityTypes, taxRegime }, ... }
 *   balByJurisdiction - { UK: { balances: { GBP: n, EUR: n } }, ... }
 *   balByEntity       - { entityId: { entity, balances, accounts } }
 *   wealthAssets      - array of asset objects from wealthAPI.getAssets()
 *   entities          - full entity list from the store
 *   onEntitySelect    - callback(entityId)
 *   convertBalances   - (amountsMap, targetCurrency) => number
 *   formatCurrency    - (value, currency) => string
 *
 * Uses function Foo(props) with body destructuring per 147f605 guard.
 */

import { motion } from 'framer-motion';
import {
  Globe,
  Wallet,
  Building2,
  ChevronRight,
  Briefcase,
  Home,
  TrendingUp,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { BankLogo } from '../BankLogo';
import { convertCurrencyDisplay } from '../../lib/displayCurrency';

function JurisdictionCard(props) {
  var configKey = props.configKey;
  var config = props.config;
  var balByJurisdiction = props.balByJurisdiction;
  var balByEntity = props.balByEntity;
  var wealthAssets = props.wealthAssets;
  var entities = props.entities;
  var onEntitySelect = props.onEntitySelect;
  var convertBalances = props.convertBalances;
  var formatCurrency = props.formatCurrency;
  var index = props.index;

  var jurBucket = balByJurisdiction[configKey] || {};
  var jurBal = jurBucket.balances || {};
  var jurBalConverted = convertBalances(jurBal, config.currency);
  var allJurEntities = entities.filter(
    function (e) { return config.entityTypes.includes(e.type) && e.country === config.country; }
  );
  // Render whenever the store lists entities for this jurisdiction OR the
  // predicate bucketed cash here, so a card's figure is never hidden away from
  // the Cash Now == sum-of-cards reconciliation.
  var hasBucketBalance = Object.keys(jurBal).length > 0;
  if (allJurEntities.length === 0 && !hasBucketBalance) return null;

  // Group wealth assets by jurisdiction
  var jurAssets = wealthAssets.filter(function (a) {
    var country = a.country || ({ EUR: 'France', GBP: 'UK', USD: 'UK', DKK: 'France' }[a.currency] || 'UK');
    return country === config.country || (configKey === 'Personal' && country === 'Personal');
  });
  var assetTypeLabels = { real_estate: 'Real Estate', trading: 'Trading', business_equity: 'Business Equity', cash: 'Cash' };
  var assetTypeIcons = { real_estate: Home, trading: TrendingUp, business_equity: Briefcase, cash: Wallet };
  var assetDisplayCurrency = config.currency;
  var assetsByType = {};
  for (var i = 0; i < jurAssets.length; i++) {
    var a = jurAssets[i];
    var t = a.type || 'cash';
    var val = convertCurrencyDisplay(a.current_value || 0, a.currency || 'GBP', assetDisplayCurrency);
    assetsByType[t] = (assetsByType[t] || 0) + val;
  }
  var totalAssets = Object.values(assetsByType).reduce(function (s, v) { return s + v; }, 0);

  // Multi-currency breakdown string
  var currencyBreakdown = Object.entries(jurBal)
    .filter(function (pair) { return pair[1] > 0; })
    .sort(function (a, b) { return b[1] - a[1]; });

  // Accounts in this jurisdiction come from the predicate bucket (the same
  // membership that drives the total), not a store-side entity filter.
  var allJurAccounts = jurBucket.accounts || [];

  return (
    <motion.div
      key={configKey}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <Card className="h-full overflow-hidden group hover:shadow-lg transition-all duration-300">
        {/* Colored header strip */}
        <div
          className="px-5 py-4"
          style={{ background: 'linear-gradient(135deg, ' + config.color + '14, ' + config.color + '08)', borderBottom: '2px solid ' + config.color + '25' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm"
                style={{ backgroundColor: config.color + '15', border: '1px solid ' + config.color + '30' }}
              >
                {config.flag}
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight">{config.name}</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">{config.taxRegime}</p>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="font-mono text-[10px] shrink-0 px-2 py-0.5"
              style={{ backgroundColor: config.color + '15', color: config.color, border: '1px solid ' + config.color + '30' }}
            >
              {config.currency}
            </Badge>
          </div>
        </div>

        <CardContent className="p-5 space-y-5">
          {/* Bank Balance total */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5">
                <Wallet className="h-3 w-3" />
                Bank Balance
              </p>
              {allJurAccounts.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {allJurAccounts.length} account{allJurAccounts.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold font-mono tracking-tight" style={{ color: jurBalConverted > 0 ? undefined : 'hsl(var(--muted-foreground))' }}>
              {formatCurrency(jurBalConverted, config.currency)}
            </p>
            {/* Multi-currency breakdown pills */}
            {currencyBreakdown.length > 1 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {currencyBreakdown.map(function (pair) {
                  var cur = pair[0];
                  var amt = pair[1];
                  var symbols = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', DKK: 'kr' };
                  return (
                    <span key={cur} className="text-[10px] font-mono bg-muted/60 text-muted-foreground rounded-md px-1.5 py-0.5">
                      {symbols[cur] || cur}{amt.toLocaleString('en-GB', { minimumFractionDigits: 0 })}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Entity bank balances with accounts */}
          <div className="space-y-1">
            {allJurEntities.map(function (ent) {
              var entData = balByEntity[ent.id];
              var entBalConverted = entData ? convertBalances(entData.balances, config.currency) : 0;
              var entBalStr = entData && entBalConverted > 0 ? formatCurrency(entBalConverted, config.currency) : null;
              var entAccounts = (entData && entData.accounts) || [];
              return (
                <div key={ent.id} className="rounded-lg">
                  <button
                    onClick={function () { onEntitySelect(ent.id); }}
                    className="flex items-center justify-between w-full text-left py-2 px-2.5 rounded-lg hover:bg-muted/60 transition-colors group/entity"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="text-sm font-medium truncate">{ent.name}</span>
                    </span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {/* Show bank logos */}
                      {entAccounts.length > 0 && (
                        <span className="flex items-center -space-x-1">
                          {[].concat(new Set(entAccounts.map(function (acc) { return acc.bank_name; }))).filter(function (v, i, arr) { return arr.indexOf(v) === i; }).slice(0, 3).map(function (bank) {
                            return (
                              <span key={bank} className="ring-2 ring-background rounded">
                                <BankLogo bankName={bank} size="sm" />
                              </span>
                            );
                          })}
                        </span>
                      )}
                      <span className="text-xs font-mono font-semibold ml-1">
                        {entBalStr != null ? entBalStr : <span className="text-muted-foreground">{'\u2014'}</span>}
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover/entity:opacity-100 transition-opacity" />
                    </span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Assets held */}
          {totalAssets > 0 && (
            <div className="pt-3 border-t border-border/60">
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5">
                  <Briefcase className="h-3 w-3" />
                  Assets
                </p>
                <p className="text-sm font-bold font-mono tracking-tight">
                  {formatCurrency(totalAssets, assetDisplayCurrency)}
                </p>
              </div>
              {/* Visual breakdown bar */}
              <div className="h-2 rounded-full bg-muted/40 overflow-hidden flex mb-2.5">
                {Object.entries(assetsByType)
                  .sort(function (a, b) { return b[1] - a[1]; })
                  .map(function (pair, i) {
                    var type = pair[0];
                    var value = pair[1];
                    var colors = { real_estate: '#10B981', trading: '#3B82F6', business_equity: '#8B5CF6', cash: '#F59E0B' };
                    return (
                      <div
                        key={type}
                        className="h-full transition-all duration-500"
                        style={{
                          width: (value / totalAssets) * 100 + '%',
                          backgroundColor: colors[type] || '#6B7280',
                          borderRight: i < Object.keys(assetsByType).length - 1 ? '1px solid hsl(var(--background))' : 'none',
                        }}
                      />
                    );
                  })}
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {Object.entries(assetsByType)
                  .sort(function (a, b) { return b[1] - a[1]; })
                  .map(function (pair) {
                    var type = pair[0];
                    var value = pair[1];
                    var colors = { real_estate: '#10B981', trading: '#3B82F6', business_equity: '#8B5CF6', cash: '#F59E0B' };
                    return (
                      <div key={type} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: colors[type] || '#6B7280' }} />
                          <span>{assetTypeLabels[type] || type}</span>
                        </span>
                        <span className="font-mono font-medium">{formatCurrency(value, assetDisplayCurrency)}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Catch-all card for accounts that resolve to no jurisdiction. Rendered last so
// the jurisdiction totals plus this bucket always sum to the group cash total.
function UnassignedCard(props) {
  var unassigned = props.unassigned;
  var convertBalances = props.convertBalances;
  var formatCurrency = props.formatCurrency;
  var index = props.index;

  var balances = (unassigned && unassigned.balances) || {};
  var accounts = (unassigned && unassigned.accounts) || [];
  var converted = convertBalances(balances, 'GBP');
  var breakdown = Object.entries(balances)
    .filter(function (pair) { return pair[1] !== 0; })
    .sort(function (a, b) { return Math.abs(b[1]) - Math.abs(a[1]); });
  if (breakdown.length === 0 && accounts.length === 0) return null;

  var color = '#6B7280';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <Card className="h-full overflow-hidden border-dashed">
        <div
          className="px-5 py-4"
          style={{ background: 'linear-gradient(135deg, ' + color + '14, ' + color + '08)', borderBottom: '2px solid ' + color + '25' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm"
                style={{ backgroundColor: color + '15', border: '1px solid ' + color + '30' }}
              >
                {'❓'}
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight">Unassigned</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">No jurisdiction mapping</p>
              </div>
            </div>
          </div>
        </div>
        <CardContent className="p-5 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5">
                <Wallet className="h-3 w-3" />
                Bank Balance
              </p>
              {accounts.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {accounts.length} account{accounts.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-2xl font-bold font-mono tracking-tight">
              {formatCurrency(converted, 'GBP')}
            </p>
            {breakdown.length > 1 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {breakdown.map(function (pair) {
                  var cur = pair[0];
                  var amt = pair[1];
                  var symbols = { GBP: '£', EUR: '€', USD: '$', DKK: 'kr' };
                  return (
                    <span key={cur} className="text-[10px] font-mono bg-muted/60 text-muted-foreground rounded-md px-1.5 py-0.5">
                      {symbols[cur] || cur}{amt.toLocaleString('en-GB', { minimumFractionDigits: 0 })}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Cash on accounts whose entity or jurisdiction is unresolved. Counted here so the jurisdiction totals stay complete.
          </p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function ByJurisdictionView(props) {
  var countryConfig = props.countryConfig;
  var balByJurisdiction = props.balByJurisdiction;
  var balByEntity = props.balByEntity;
  var unassigned = props.unassigned;
  var wealthAssets = props.wealthAssets;
  var entities = props.entities;
  var onEntitySelect = props.onEntitySelect;
  var convertBalances = props.convertBalances;
  var formatCurrency = props.formatCurrency;

  var configCount = Object.keys(countryConfig).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          By Jurisdiction
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(countryConfig).map(function (pair, index) {
          var key = pair[0];
          var config = pair[1];
          return (
            <JurisdictionCard
              key={key}
              configKey={key}
              config={config}
              balByJurisdiction={balByJurisdiction}
              balByEntity={balByEntity}
              wealthAssets={wealthAssets}
              entities={entities}
              onEntitySelect={onEntitySelect}
              convertBalances={convertBalances}
              formatCurrency={formatCurrency}
              index={index}
            />
          );
        })}
        {unassigned && (
          <UnassignedCard
            unassigned={unassigned}
            convertBalances={convertBalances}
            formatCurrency={formatCurrency}
            index={configCount}
          />
        )}
      </div>
    </div>
  );
}
